/**
 * Create a same-day partner check-in queue entry (4-digit code).
 * Uses franchise-flow token against Enterprise salon-admin API.
 */

import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import {
  readFranchiseFlowAuth,
  getFranchiseOrganizationId,
  getFranchiseStylistId,
  hasFranchiseFlowAuth,
  franchiseAuthHeaders,
} from '../../shared/franchiseFlowAuth';
import { guestAuthHeaders, hasGuestFlowAuth } from '../../shared/guestFlowAuth';
import { fetchAdminWithRetry } from '../../admin-shared/fetchAdminWithRetry';
import {
  checkinQueueFetchInit,
  EMPTY_QUEUE,
} from '../../admin-shared/checkinQueueFetch';
import { buildHqSessionHeaders, hqSessionFetchInit } from '../../admin-shared/hqSessionAuth';
import { PORTAL_ID } from './api';

function hasPartnerSession() {
  return Boolean(localStorage.getItem('enterprise_salon_userId'));
}

function adminBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

/** Kiosk / salon-admin API with timeout + retry (502/503/504, network blips). */
async function queueHttp(url, init = {}, retryOpts = {}) {
  const timeoutMs = init.timeoutMs ?? 25000;
  const { timeoutMs: _drop, ...rest } = init;
  return fetchAdminWithRetry(url, { ...rest, timeoutMs }, retryOpts);
}

function authHeaders(extra = {}) {
  return buildHqSessionHeaders(PORTAL_ID, extra);
}

function kioskFetchHeaders(extra = {}) {
  return franchiseAuthHeaders({ 'Content-Type': 'application/json', ...extra });
}

function stylistPayload(extra = {}) {
  const auth = readFranchiseFlowAuth();
  const stylistId =
    extra.stylistId ||
    extra.stylist_id ||
    getFranchiseStylistId() ||
    auth?.stylist_id ||
    auth?.login_account_id ||
    null;
  const stylistName =
    extra.stylistName ||
    extra.stylist_name ||
    auth?.full_name ||
    auth?.username ||
    null;
  return {
    stylist_id: stylistId || undefined,
    stylist_name: stylistName || undefined,
  };
}

/**
 * @param {{
 *   faceUserId?: string,
 *   customerName?: string,
 *   mobile?: string,
 *   selectedServices?: array,
 *   customerSnapshot?: object,
 *   organizationId?: string,
 *   source?: string,
 *   startService?: boolean,
 *   autoComplete?: boolean,
 *   referenceToken?: string,
 *   stylistId?: string,
 *   stylistName?: string,
 * }} payload
 */
function queueWriteInit(method, body) {
  if (hasGuestFlowAuth()) {
    return {
      method,
      headers: guestAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    };
  }
  if (hasFranchiseFlowAuth()) {
    return {
      method,
      credentials: 'include',
      headers: kioskFetchHeaders(),
      body: JSON.stringify(body),
    };
  }
  return hqSessionFetchInit(PORTAL_ID, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Stylist mobile lookup — FRANCHISE_FLOW JWT only (org scoped). */
export async function lookupStylistCustomerByMobile(mobile) {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) {
    throw new Error('Stylist session required');
  }
  const res = await queueHttp(`${base}/salon/stylist/lookup-mobile`, {
    method: 'POST',
    headers: kioskFetchHeaders(),
    body: JSON.stringify({ mobile: String(mobile || '').trim() }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 404 || data?.status === 'not_found') {
    const err = new Error(data?.message || 'No profile found for this mobile number');
    err.code = 'NOT_FOUND';
    err.payload = data;
    throw err;
  }
  if (res.status === 409 || data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this mobile number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.payload = data;
    throw err;
  }
  if (!res.ok || !data?.success || !data?.user) {
    const err = new Error(data?.message || data?.error || `Lookup failed (${res.status})`);
    err.payload = data;
    throw err;
  }
  return data;
}

/** Today's engagement bookings for this outlet (booked status). */
export async function fetchTodayEngagementBookings(day) {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) {
    throw new Error('Stylist session required');
  }
  const qs = day ? `?day=${encodeURIComponent(String(day).slice(0, 10))}` : '';
  const res = await queueHttp(`${base}/salon/stylist/today-bookings${qs}`, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
    headers: kioskFetchHeaders(),
    timeoutMs: 25000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || `Failed to load bookings (${res.status})`);
  }
  return Array.isArray(data.bookings) ? data.bookings : [];
}

/** Check in a guest from an engagement appointment (CRM/local lookup via booking phone). */
export async function checkinFromEngagementBooking(appointmentId, { faceUserId } = {}) {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) {
    throw new Error('Stylist session required');
  }
  const body = {
    appointment_id: appointmentId,
    appointmentId,
  };
  if (faceUserId) {
    body.face_user_id = faceUserId;
    body.faceUserId = faceUserId;
  }
  const res = await queueHttp(`${base}/salon/stylist/checkin-from-booking`, {
    method: 'POST',
    credentials: 'include',
    headers: kioskFetchHeaders(),
    body: JSON.stringify(body),
    timeoutMs: 35000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.booking = data?.booking;
    err.payload = data;
    throw err;
  }
  if (res.status === 400 && data?.code === 'PHONE_REQUIRED') {
    const err = new Error(data?.error || 'Phone required');
    err.code = 'PHONE_REQUIRED';
    err.booking = data?.booking;
    throw err;
  }
  if (!res.ok || !data?.success || !data?.checkin?.id) {
    throw new Error(data?.error || data?.message || `Check-in failed (${res.status})`);
  }
  return data;
}

export async function createPartnerCheckinQueue(payload = {}) {
  const base = adminBase();
  if (!base) {
    throw new Error('Salon admin API is not configured');
  }

  const orgId =
    payload.organizationId ||
    payload.organization_id ||
    getFranchiseOrganizationId() ||
    '';
  if (!orgId) {
    throw new Error('Organization is required for partner check-in');
  }

  const startService = Boolean(
    payload.startService ??
      payload.start_service ??
      payload.autoComplete ??
      payload.auto_complete,
  );

  const body = {
    organization_id: orgId,
    face_user_id: payload.faceUserId || payload.face_user_id || undefined,
    customer_name: payload.customerName || payload.customer_name || '',
    mobile: payload.mobile || '',
    selected_services: payload.selectedServices || payload.selected_services || [],
    customer_snapshot: payload.customerSnapshot || payload.customer_snapshot || {},
    source: payload.source || 'face_identity',
    start_service: startService,
    // Legacy alias for older backends
    auto_complete: startService,
    force_new: Boolean(payload.forceNew || payload.force_new) || undefined,
    reference_token: payload.referenceToken || payload.reference_token || undefined,
    assigned_staff: payload.assignedStaff || payload.assigned_staff || undefined,
    ...stylistPayload(payload),
  };

  const res = await queueHttp(`${base}/checkin-queue`, {
    ...queueWriteInit('POST', body),
    timeoutMs: 15000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Failed to create check-in');
  }
  const checkin = data.checkin || null;
  if (checkin && data.reused) {
    checkin.reused_open_visit = true;
  }
  return checkin;
}

export async function fetchCheckinQueue({ status = 'all', preferFranchise = false, fromDate, toDate } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const params = new URLSearchParams({ status: status || 'all' });
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const hasFranchise = preferFranchise && hasFranchiseFlowAuth();
  const hasSession = hasFranchise || hasPartnerSession();
  if (!base || !hasSession) {
    return { ...EMPTY_QUEUE };
  }

  const res = await fetchAdminWithRetry(
    `${base}/checkin-queue?${params.toString()}`,
    checkinQueueFetchInit(PORTAL_ID, undefined, {
      headers: kioskFetchHeaders(),
      timeoutMs: 25000,
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      return { ...EMPTY_QUEUE };
    }
    throw new Error(data?.error || 'Failed to load check-ins');
  }
  return data;
}

/**
 * Partner check-in analytics for the signed-in franchise (JWT organization_id).
 * @param {{ fromDate?: string, toDate?: string }} range
 */
export async function fetchCheckinAnalytics({ fromDate, toDate } = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await queueHttp(
    `${base}/checkin-queue/analytics${qs}`,
    { ...hqSessionFetchInit(PORTAL_ID), timeoutMs: 25000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load check-in analytics');
  }
  return data;
}

export async function fetchActiveVisit({
  faceUserId,
  visitReference,
  checkinId,
  includeClosed = false,
  timeoutMs = 45000,
} = {}) {
  const base = adminBase();
  if (!base) return null;
  if (!hasFranchiseFlowAuth()) return null;

  const params = new URLSearchParams();
  if (faceUserId) params.set('faceUserId', String(faceUserId));
  if (visitReference) params.set('visitReference', String(visitReference));
  if (checkinId != null && String(checkinId).trim() !== '') {
    params.set('checkinId', String(checkinId));
  }
  if (includeClosed) params.set('includeClosed', '1');
  if (![...params.keys()].length) return null;

  const res = await queueHttp(
    `${base}/checkin-queue/active-visit?${params}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs,
    },
    { attempts: 2 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return null;
  return data.checkin || null;
}

export async function commitPassportWelcomeProfileSaved(checkinId) {
  const base = adminBase();
  if (!base || !checkinId) return null;
  if (!hasFranchiseFlowAuth() && !hasPartnerSession()) return null;

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/passport-welcome-profile-saved`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to sync welcome profile');
  }
  return data.checkin;
}

export async function completeCheckinQueue(checkinId, { notes } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');

  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/complete`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || undefined }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to complete check-in');
  }
  return data.checkin;
}

export async function startServiceCheckinQueue(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const body = {
    ...stylistPayload(extras),
    assigned_staff: extras.assignedStaff || extras.assigned_staff || undefined,
    selected_services: extras.selectedServices || extras.selected_services || undefined,
  };

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/start-service`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify(body),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to start service');
  }
  return data.checkin;
}

export async function assignStaffToCheckin(checkinId, assignedStaff = []) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }
  const init = hasFranchiseFlowAuth()
    ? {
        method: 'POST',
        headers: kioskFetchHeaders(),
        body: JSON.stringify({ assigned_staff: assignedStaff }),
      }
    : hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_staff: assignedStaff }),
      });
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/assign-staff`,
    { ...init, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to assign staff');
  }
  return data.checkin;
}

export async function updateCheckinServices(checkinId, { selectedServices = [], assignedStaff } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }
  const body = {
    selected_services: selectedServices,
    assigned_staff: assignedStaff,
  };
  const init = hasFranchiseFlowAuth()
    ? {
        method: 'PUT',
        headers: kioskFetchHeaders(),
        body: JSON.stringify(body),
      }
    : hqSessionFetchInit(PORTAL_ID, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/services`,
    { ...init, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update services');
  }
  return data.checkin;
}

export async function linkFaceToCheckin(checkinId, { faceUserId, customerSnapshot } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  if (!faceUserId) throw new Error('faceUserId is required');

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/link-face`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({
        face_user_id: faceUserId,
        customer_snapshot: customerSnapshot || undefined,
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to link face profile');
  }
  return data.checkin;
}

export async function assignStylistToCheckin(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/assign-stylist`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stylistPayload(extras)),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to assign stylist');
  }
  return data.checkin;
}

export async function cancelCheckinQueue(checkinId, { force = false } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/cancel`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: Boolean(force) }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to cancel check-in');
  }
  return data.checkin;
}

export async function reopenCheckinQueue(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/reopen`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to reopen check-in');
  }
  return data.checkin;
}

export async function fetchOpenVisits() {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) return [];
  const res = await queueHttp(`${base}/checkin-queue/open-visits`, {
    method: 'GET',
    headers: kioskFetchHeaders(),
    timeoutMs: 12000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return [];
  const list = data.checkins || data.items || [];
  return Array.isArray(list) ? list : [];
}

export async function fetchCompletedVisits() {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) return [];

  const tryDedicated = async () => {
    const res = await queueHttp(`${base}/checkin-queue/completed-visits`, {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 12000,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) return null;
    const list = data.checkins || data.items || [];
    return Array.isArray(list) ? list : [];
  };

  try {
    const dedicated = await tryDedicated();
    if (dedicated) return dedicated;
  } catch {
    /* fall through */
  }

  try {
    const data = await fetchCheckinQueue({ status: 'completed', preferFranchise: true });
    const list = data?.checkins || data?.items || [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export async function fetchCheckinRecommendedProducts(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/recommended-products`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load recommended products');
  }
  return data;
}

export async function setCheckinProductPreview(checkinId, active = true) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/product-preview`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({ active: Boolean(active) }),
      timeoutMs: 12000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update product preview');
  }
  return data;
}

export async function fetchCheckinReportSessions(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/report-sessions`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Could not load report sessions');
  }
  return data;
}

export async function markCheckinAnalysisProductsReady(
  checkinId,
  {
    source = 'analysis',
    salonReference = '',
    hairInsightSessionId = '',
    productCount = null,
  } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const body = {
    source,
    salon_reference: salonReference || undefined,
    hair_insight_session_id: hairInsightSessionId || undefined,
  };
  if (productCount != null && Number.isFinite(Number(productCount))) {
    body.product_count = Math.max(0, Number(productCount));
  }
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/analysis-products-ready`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify(body),
      timeoutMs: 12000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to mark analysis products ready');
  }
  return data;
}

export async function saveCheckinProductCart(checkinId, items, { source = 'lobby' } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/product-cart`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({ items: Array.isArray(items) ? items : [], source }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to save product cart');
  }
  return data;
}

export async function placeCheckinProductOrder(checkinId, items, { source = 'lobby' } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/place-product-order`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({
        items: items == null ? undefined : Array.isArray(items) ? items : [],
        source,
      }),
      timeoutMs: 20000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to place product order');
  }
  return data;
}

export async function fetchActiveProductPreview() {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) return { active: false };
  const res = await queueHttp(`${base}/checkin-queue/product-preview-active`, {
    method: 'GET',
    headers: kioskFetchHeaders(),
    timeoutMs: 12000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return { active: false };
  return data;
}

export async function mergePendingServicesToCheckin(
  checkinId,
  selectedServices,
  {
    analysisSource,
    startServices = false,
    assignedStaff,
    startSelectedServices,
  } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/merge-pending-services`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({
        selected_services: selectedServices,
        analysis_source: analysisSource || undefined,
        start_services: startServices || undefined,
        assigned_staff: assignedStaff || undefined,
        start_selected_services: startSelectedServices || undefined,
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to sync analysis services');
  }
  return data.checkin;
}

export async function completeServiceLines(checkinId, lineIds, { notes } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const ids = Array.isArray(lineIds) ? lineIds : [];
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/complete-services`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({ line_ids: ids, notes: notes || undefined }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to complete service');
  }
  return data.checkin;
}

export async function cancelServiceLines(checkinId, lineIds) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');
  const ids = Array.isArray(lineIds) ? lineIds : [];
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/cancel-services`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify({ line_ids: ids }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to cancel service');
  }
  return data.checkin;
}

export async function lookupCheckinByCode(
  code,
  { complete = false, cancel = false, startService = false } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const params = new URLSearchParams();
  if (cancel) params.set('cancel', '1');
  else if (complete) params.set('complete', '1');
  else if (startService) params.set('start_service', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await queueHttp(
    `${base}/checkin-queue/by-code/${encodeURIComponent(String(code).trim())}${qs}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Invalid check-in code');
  }
  return data.checkin;
}

/** Release an active queue entry when the guest abandons Welcome (Change code / leave). */
export async function releaseCheckinByCode(code) {
  return lookupCheckinByCode(code, { complete: false, cancel: true });
}

export async function fetchGuestVisitSummary({ checkinId, faceUserId, timeoutMs = 45000 } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const params = new URLSearchParams();
  if (checkinId) params.set('checkin_id', String(checkinId));
  if (faceUserId) params.set('face_user_id', String(faceUserId));
  const res = await queueHttp(
    `${base}/checkin-queue/guest-visit-summary?${params}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      credentials: 'include',
      timeoutMs,
    },
    { attempts: 2 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load guest summary');
  }
  return {
    summary: data.summary || {},
    serviceHistory: data.service_history || data.serviceHistory || [],
  };
}

export async function patchCheckinVisitIntent(checkinId, visitIntent) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const res = await queueHttp(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/visit-intent`,
    {
      method: 'PATCH',
      headers: kioskFetchHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ visit_intent: visitIntent }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update visit intent');
  }
  return data.checkin;
}

export async function fetchUserServiceHistory(faceUserId, limit = 30) {
  const base = adminBase();
  if (!base || !faceUserId) return [];
  const lim = Math.max(1, Math.min(Number(limit) || 30, 100));
  const res = await queueHttp(
    `${base}/register-services/history/${encodeURIComponent(String(faceUserId).trim())}?limit=${lim}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      credentials: 'include',
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'Failed to load service history');
  }
  return Array.isArray(data.items) ? data.items : Array.isArray(data.history) ? data.history : [];
}

/** Salon hub: check-in id → latest reel summaries for guest list. */
export async function fetchReelsDeskIndex() {
  const base = adminBase();
  if (!base || !hasFranchiseFlowAuth()) return {};
  const res = await queueHttp(`${base}/checkin-queue/reels-desk-index`, {
    method: 'GET',
    headers: kioskFetchHeaders(),
    credentials: 'include',
    timeoutMs: 20000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return {};
  const map = data.reels_by_checkin;
  return map && typeof map === 'object' ? map : {};
}

/** Trigger browser download for a rendered reel (franchise-flow presign). */
export async function downloadLobbyReel(sessionId) {
  const base = adminBase();
  if (!base || !sessionId) throw new Error('Reel unavailable');
  const res = await queueHttp(
    `${base}/franchise-flow/reels/${encodeURIComponent(sessionId)}/download`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      credentials: 'include',
      timeoutMs: 25000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Download failed');
  const url = data.download_url;
  if (!url) throw new Error('Download link unavailable');
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.target = '_blank';
  anchor.download = data.filename || `enterprise-reel-${sessionId}.mp4`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
